import React, { useEffect, createContext, useContext } from 'react';
import { useAuth } from 'react-oidc-context';
import { setTokenCallback, setSigninCallback } from '../lib/api/apiClient';
import { setTokenGetter } from '../lib/api/client';

interface ApiAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: ReturnType<typeof useAuth>['user'];
  error: ReturnType<typeof useAuth>['error'];
}

const ApiAuthContext = createContext<ApiAuthContextType | null>(null);

export const useApiAuth = (): ApiAuthContextType => {
  const context = useContext(ApiAuthContext);
  if (!context) {
    throw new Error('useApiAuth must be used within an ApiAuthProvider');
  }
  return context;
};

interface ApiAuthProviderProps {
  children: React.ReactNode;
}

export const ApiAuthProvider: React.FC<ApiAuthProviderProps> = ({ children }) => {
  const auth = useAuth();

  useEffect(() => {
    const getToken = () => {
      if (auth.isAuthenticated && auth.user) {
        return auth.user.access_token;
      }
      return null;
    };

    setTokenCallback(getToken);
    setTokenGetter(getToken);

    setSigninCallback(() => {
      if (!auth.isLoading) {
        auth.signinRedirect({
          redirect_uri: window.location.origin,
        });
      }
    });
  }, [auth]);

  const value: ApiAuthContextType = {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user,
    error: auth.error,
  };

  return (
    <ApiAuthContext.Provider value={value}>
      {children}
    </ApiAuthContext.Provider>
  );
};
